import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { clientsApi } from './client.api';

export const queryKeys = {
    all: ['clients'] as const,
    lists: () => [...queryKeys.all, 'list'] as const,
    list: (filters: { search?: string; cursor?: string | null; page_size?: number }) =>
        [...queryKeys.lists(), filters] as const,
    searches: () => [...queryKeys.all, 'search'] as const,
    search: (filters: { search: string; page_size: number }) =>
        [...queryKeys.searches(), filters] as const,
    details: () => [...queryKeys.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.details(), id] as const,
    profile: (id: string) => [...queryKeys.profilesOfAnyClient(), id] as const,
    contactsOfAnyClient: () => [...queryKeys.all, 'contacts'] as const,
    contacts: (id: string) => [...queryKeys.contactsOfAnyClient(), id] as const,
    profilesOfAnyClient: () => [...queryKeys.all, 'profile'] as const,
    contactCandidatesOfAnyClient: () => [...queryKeys.all, 'contact-candidates'] as const,
    contactCandidates: (id: string, filters: { search: string; page_size: number }) =>
        [...queryKeys.contactCandidatesOfAnyClient(), id, filters] as const,
    person: (id: string) => [...queryKeys.all, 'person', id] as const,
    contactProfile: (personId: string) => [...queryKeys.all, 'contact-profile', personId] as const,
    relationshipLists: () => [...queryKeys.all, 'relationships'] as const,
    relationships: (clientId: string) => [...queryKeys.relationshipLists(), clientId] as const,
};

export function clientByIdOptions(clientId: string) {
    return queryOptions({
        queryKey: queryKeys.detail(clientId),
        queryFn: ({ signal }) => clientsApi.getById(clientId, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function clientProfileOptions(clientId: string) {
    return queryOptions({
        queryKey: queryKeys.profile(clientId),
        queryFn: ({ signal }) => clientsApi.getProfile(clientId, signal),
    });
}

export function clientContactsOptions(clientId: string) {
    return queryOptions({
        queryKey: queryKeys.contacts(clientId),
        queryFn: ({ signal }) => clientsApi.getContacts(clientId, signal),
    });
}

// The server refuses an empty search, so nothing is fetched until a term exists (FR-011).
export function clientContactCandidatesOptions(
    clientId: string,
    searchTerm: string,
    pageSize: number = 20,
) {
    const search = searchTerm.trim();

    return queryOptions({
        queryKey: queryKeys.contactCandidates(clientId, { search, page_size: pageSize }),
        queryFn: ({ signal }) =>
            clientsApi.getContactCandidates(clientId, { search, page_size: pageSize }, signal),
        enabled: search.length > 0,
        placeholderData: (previous) => previous,
    });
}

export function clientPersonOptions(personId: string) {
    return queryOptions({
        queryKey: queryKeys.person(personId),
        queryFn: ({ signal }) => clientsApi.getPerson(personId, signal),
    });
}

export function contactProfileOptions(personId: string) {
    return queryOptions({
        queryKey: queryKeys.contactProfile(personId),
        queryFn: ({ signal }) => clientsApi.getContactProfile(personId, signal),
        // A contact's clients change from other charts, so every open refetches rather than trusting the cache.
        staleTime: 0,
    });
}

export function clientRelationshipsOptions(clientId: string) {
    return queryOptions({
        queryKey: queryKeys.relationships(clientId),
        queryFn: ({ signal }) => clientsApi.getRelationships(clientId, signal),
    });
}

export function clientCountOptions() {
    return queryOptions({
        queryKey: queryKeys.all,
        queryFn: ({ signal }) => clientsApi.getCount(signal),
        staleTime: 15 * 60 * 1000, // 15 minutes
    });
}

export function clientSearchOptions(
    searchTerm: string,
    enabled: boolean = true,
    pageSize: number = 20,
) {
    return queryOptions({
        queryKey: queryKeys.search({ search: searchTerm, page_size: pageSize }),
        queryFn: ({ signal }) =>
            clientsApi.list({ search: searchTerm, page_size: pageSize }, signal),
        enabled,
        placeholderData: (previous) => previous,
    });
}

export function clientListOptions() {
    return infiniteQueryOptions({
        queryKey: queryKeys.list({ cursor: null }),
        queryFn: async ({ pageParam, signal }) => {
            return await clientsApi.list({ cursor: pageParam }, signal);
        },
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
    });
}
